// Inventory search that understands how shoppers type ("used suv", "ford truck under 20k",
// "side by side", "chevy 4x4"). Every word must match somewhere on the unit; everyday words
// are mapped to how inventory is stored. Returns ranked matches, or — when nothing matches —
// the closest units so the shopper never hits a dead end.

// Multi-word phrases rewritten before splitting into words.
const PHRASES = [
  [/\bside[\s-]*by[\s-]*sides?\b|\bsxs\b/g, ' utv '],
  [/\b(four|4)[\s-]*wheelers?\b/g, ' atv '],
  [/\bsport[\s-]*utility( vehicles?)?\b/g, ' suv '],
  [/\bpick[\s-]*ups?\b/g, ' truck '],
  [/\bpower[\s-]*sports?\b/g, ' powersports '],
  [/\bf[\s-]?(150|250|350)\b/g, ' f$1 '],
  [/\bfour[\s-]*wheel[\s-]*drive\b/g, ' 4x4 '],
  [/\ball[\s-]*wheel[\s-]*drive\b/g, ' awd '],
];

// word -> any of these must appear on the unit
const SYNONYMS = {
  suv: ['suv', 'crossover'], crossover: ['crossover', 'suv'],
  truck: ['truck', 'pickup'],
  car: ['sedan', 'coupe', 'hatchback', 'car', 'wagon', 'convertible'],
  sedan: ['sedan'],
  atv: ['atv', 'quad'], quad: ['atv', 'quad'],
  utv: ['utv', 'side-by-side', 'sxs'],
  powersports: ['power sports', 'atv', 'utv', 'snowmobile', 'motorcycle'],
  sled: ['snowmobile'], snowmobile: ['snowmobile'],
  boat: ['boat', 'marine', 'pontoon'],
  chevy: ['chevrolet', 'chevy'], vw: ['volkswagen'], merc: ['mercedes'],
  '4x4': ['4wd', '4x4', 'awd'], '4wd': ['4wd', '4x4', 'awd'], awd: ['awd', '4wd'],
  gray: ['grey', 'gray'], grey: ['grey', 'gray'],
  auto: ['automatic'], automatic: ['automatic'], stick: ['manual'],
};

const STOP = new Set(['a', 'an', 'the', 'for', 'sale', 'with', 'and', 'in', 'on', 'near', 'me', 'winnipeg', 'buy',
  'cheap', 'vehicle', 'vehicles', 'looking', 'want', 'i', 'im', 'any', 'or', 'of', 'to']);

// Field weights: a hit in the title/make/model beats a hit in colour or stock #.
const FIELDS = [
  ['title', 3], ['make', 3], ['model', 3], ['bodyType', 3], ['trim', 2], ['year', 2], ['category', 2],
  ['condition', 2], ['exteriorColor', 1], ['drivetrain', 1], ['transmission', 1], ['fuelType', 1],
  ['stockNumber', 1], ['vin', 1],
];

const norm = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const compact = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
const money = (n, k) => Number(String(n).replace(/,/g, '')) * (k ? 1000 : 1);

// Pull "under 20k" / "over $15,000" / "$12k" out of the query.
export function parseQuery(raw) {
  let q = ` ${String(raw || '').toLowerCase()} `;
  let maxPrice = null; let minPrice = null;
  q = q.replace(/\b(under|below|less than|max|up to|<)\s*\$?\s*(\d[\d,]*)\s*(k)?\b/g, (_, __, n, k) => { maxPrice = money(n, k); return ' '; });
  q = q.replace(/\b(over|above|more than|min|from|>)\s*\$?\s*(\d[\d,]*)\s*(k)?\b/g, (_, __, n, k) => { minPrice = money(n, k); return ' '; });
  q = q.replace(/\$\s*(\d[\d,]*)\s*(k)?\b|\b(\d{1,3})k\b/g, (_, n, k, n2) => { maxPrice = n2 ? money(n2, true) : money(n, k); return ' '; });
  for (const [re, to] of PHRASES) q = q.replace(re, to);
  const words = norm(q).split(' ').filter((w) => w && !STOP.has(w));
  return { words, maxPrice, minPrice };
}

const alternatives = (w) => {
  const alts = new Set(SYNONYMS[w] || [w]);
  if (w.length > 3 && w.endsWith('s')) (SYNONYMS[w.slice(0, -1)] || [w.slice(0, -1)]).forEach((a) => alts.add(a));
  return [...alts];
};

function prepare(v) {
  return FIELDS.map(([f, weight]) => {
    let val = v[f];
    if (f === 'category' && val === 'Power Sports') val = 'Power Sports powersports';
    return { weight, text: ` ${norm(val)}`, flat: compact(val) };
  });
}

// Best weight of a field that contains this word (word-start match, so "red" ≠ "credit"; "f150" matches "F-150").
function wordScore(fields, w) {
  let best = 0;
  for (const alt of alternatives(w)) {
    const a = norm(alt); const flat = compact(alt);
    for (const f of fields) {
      if (f.weight <= best) continue;
      const hit = f.text.includes(` ${a}`) || (/\d/.test(flat) && /[a-z]/.test(flat) && f.flat.includes(flat));
      if (hit) best = f.weight;
    }
  }
  return best;
}

const priceOk = (v, { maxPrice, minPrice }) => {
  const p = Number(v.price) || 0;
  if ((maxPrice || minPrice) && !p) return false; // "Call for price" can't satisfy a budget
  return (!maxPrice || p <= maxPrice) && (!minPrice || p >= minPrice);
};

/**
 * @returns {{ results: object[], exact: boolean, parsed: object }}
 *   results: matching units, best match first. exact=false means nothing matched every
 *   word, and results are the closest units instead (most words matched / nearest price).
 */
export function searchVehicles(vehicles, query) {
  const parsed = parseQuery(query);
  const { words } = parsed;
  if (!words.length && !parsed.maxPrice && !parsed.minPrice) return { results: vehicles, exact: true, parsed };

  const scored = vehicles.map((v) => {
    const fields = prepare(v);
    const per = words.map((w) => wordScore(fields, w));
    return { v, hits: per.filter(Boolean).length, score: per.reduce((a, b) => a + b, 0), price: priceOk(v, parsed) };
  });
  const rank = (a, b) => b.hits - a.hits || b.score - a.score;

  const exact = scored.filter((s) => s.hits === words.length && s.price).sort(rank);
  if (exact.length) return { results: exact.map((s) => s.v), exact: true, parsed };

  // Closest: units matching some of the words (within budget if one was given) …
  let near = scored.filter((s) => s.hits > 0 && (s.price || !words.length)).sort(rank);
  if (!near.length) near = scored.filter((s) => s.hits > 0).sort(rank);
  // … or, for a budget-only search, the cheapest units above it.
  if (!near.length && (parsed.maxPrice || parsed.minPrice)) {
    near = scored.filter((s) => Number(s.v.price) > 0)
      .sort((a, b) => Math.abs(a.v.price - (parsed.maxPrice || parsed.minPrice)) - Math.abs(b.v.price - (parsed.maxPrice || parsed.minPrice)));
  }
  return { results: near.slice(0, 8).map((s) => s.v), exact: false, parsed };
}
